'use client';

import React, { useState } from 'react';
import { MiniQuizComponent } from '../types/content-components.types.js';
import { Check, X, HelpCircle, RotateCw } from 'lucide-react';

interface MiniQuizProps {
  component: MiniQuizComponent;
}

export const MiniQuiz: React.FC<MiniQuizProps> = ({ component }) => {
  const { props } = component;
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [showHint, setShowHint] = useState(false);
  const [hasAnswered, setHasAnswered] = useState(false);

  const handleAnswer = (index: number) => {
    setSelectedIndex(index);
    setHasAnswered(true);
  };

  const isCorrect = selectedIndex === props.correctAnswer;

  return (
    <div className="w-full">
      {/* Question */}
      <div className="bg-learn-sky-50 border border-learn-sky-200 rounded-xl p-5 mb-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-shrink-0 mt-1">
            <div className="w-8 h-8 bg-learn-sky-200 rounded-lg flex items-center justify-center">
              <span className="text-learn-sky-700 font-bold">?</span>
            </div>
          </div>
          <div className="flex-1">
            <h4 className="text-base font-semibold text-learn-sky-900 mb-2">
              Quick Check
            </h4>
            <p className="text-sm text-gray-800 leading-relaxed">
              {props.question}
            </p>
          </div>
        </div>

        {/* Hint */}
        {props.hint && !hasAnswered && (
          <button
            onClick={() => setShowHint(!showHint)}
            className="text-sm text-learn-sky-600 hover:text-learn-sky-800 flex items-center gap-2 transition-colors"
          >
            <HelpCircle className="w-4 h-4" />
            {showHint ? 'Hide Hint' : 'Show Hint'}
          </button>
        )}
        {showHint && !hasAnswered && (
          <p className="text-sm text-learn-sky-700 mt-2 ml-6 italic">
            💡 {props.hint}
          </p>
        )}
      </div>

      {/* Options */}
      <div className="space-y-2 mb-4">
        {props.options.map((option, index) => {
          const isSelected = selectedIndex === index;
          const isCorrectAnswer = index === props.correctAnswer;
          const showResult = hasAnswered;

          let buttonStyle = 'bg-white border-2 border-gray-200 text-gray-800 hover:border-gray-300';
          if (showResult) {
            if (isSelected && isCorrect) {
              buttonStyle = 'bg-green-100 border-2 border-green-400 text-green-900';
            } else if (isSelected && !isCorrect) {
              buttonStyle = 'bg-red-100 border-2 border-red-400 text-red-900';
            } else if (isCorrectAnswer) {
              buttonStyle = 'bg-green-50 border-2 border-green-300 text-green-800';
            }
          } else if (isSelected) {
            buttonStyle = 'bg-learn-sky-100 border-2 border-learn-sky-400 text-learn-sky-900';
          }

          return (
            <button
              key={index}
              onClick={() => !hasAnswered && handleAnswer(index)}
              disabled={hasAnswered}
              className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-200 ${buttonStyle} ${
                !hasAnswered ? 'cursor-pointer' : 'cursor-default'
              } flex items-center gap-3`}
            >
              <span className="flex-1">{option}</span>
              {showResult && isSelected && (
                isCorrect ? (
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                ) : (
                  <X className="w-5 h-5 text-red-600 flex-shrink-0" />
                )
              )}
              {showResult && !isSelected && isCorrectAnswer && (
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 opacity-60" />
              )}
            </button>
          );
        })}
      </div>

      {/* Explanation (after answering) */}
      {hasAnswered && (
        <div className={`${isCorrect ? 'bg-green-50 border-green-300' : 'bg-blue-50 border-blue-300'} border rounded-xl p-4 animate-slideDown`}>
          <p className="text-sm font-semibold text-gray-900 mb-2">
            {isCorrect ? '✅ Correct!' : '💡 Not quite - here\'s why:'}
          </p>
          <p className="text-sm text-gray-700 leading-relaxed">
            {props.explanation}
          </p>
        </div>
      )}

      {/* Retry Button */}
      {hasAnswered && (
        <button
          onClick={() => {
            setHasAnswered(false);
            setSelectedIndex(null);
            setShowHint(false);
          }}
          className="mt-3 text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2"
        >
          <RotateCw className="w-4 h-4" />
          Try Again
        </button>
      )}
    </div>
  );
};

